<template>
  <div class="row">
    <div class="card">
      <div class="card-body">
        <h5 class="table-heading">{{ $t("reports.filter_reports_face") }}</h5>
        <message-card
          alertType="indicator-info"
          :alertTitle="$t('reports.filter_title')"
          :alertText="$t('reports.filter_text_face')"
          id="commentcard"
        ></message-card>
        <Form
          @submit="(values) => generate(values)"
          :validation-schema="schema"
          ref="reportsForm"
        >
          <!-- Location -->
          <div class="row m-b-sm">
            <h6>Location</h6>
            <hr />

            <div
              v-if="siteGroups.length > 0"
              class="col-md-3 col-xs-6 col-xxs-12 m-b-sm"
            >
              <label for="siteGroupFilter" class="form-label">Site Group</label>
              <Multiselect
                v-model="filters.group"
                :class="{ 'multi-dark': $store.state.auth.status.dark }"
                mode="tags"
                placeholder="Select a site group"
                :close-on-select="false"
                :searchable="true"
                :create-option="false"
                :multiple="false"
                :options="siteGroups"
                label="name"
                track-by="id"
                name="siteGroup"
                @select="loadSites"
                @remove="loadSites()"
              />
              <ErrorMessage name="siteGroup" class="error-feedback" />
            </div>

            <div class="col-md-3 col-xs-6 col-xxs-12 m-b-sm">
              <label for="siteFilter" class="form-label"
                >{{ $t("events.filters.site") }}
              </label>
              <Multiselect
                v-model="filters.site"
                :class="{ 'multi-dark': $store.state.auth.status.dark }"
                mode="tags"
                placeholder="Select a site"
                :close-on-select="true"
                :searchable="true"
                :create-option="false"
                :multiple="false"
                :options="sites"
                label="name"
                track-by="id"
                name="site"
              />
              <ErrorMessage name="site" class="error-feedback" />
            </div>
          </div>

          <!-- Faces Details -->
          <div class="row m-b-sm">
            <h6>Face Details</h6>
            <hr />

            <div class="col-md-3 col-xs-6 col-xxs-12 m-b-sm">
              <label for="alertStatusFilter" class="form-label">Alert Status</label>
              <Field
                name="alert_status"
                class="form-select"
                id="alertStatusFilter"
                v-model="filters.alert_status"
                as="select"
              >
                <option value="" selected>All Status</option>
                <option value="standard">Standard</option>
                <option value="alert">Alert</option>
              </Field>
              <ErrorMessage name="alert_status" class="error-feedback" />
            </div>

            <div class="col-md-3 col-xs-6 col-xxs-12 m-b-sm">
              <label for="incidentTypeFilter" class="form-label">Incident Type</label>
              <Field
                name="incident_type"
                class="form-select"
                id="incidentTypeFilter"
                v-model="filters.incident_type"
                as="select"
              >
                <option value="" selected>All Incident Types</option>
                <option 
                  v-for="type in incidentTypes" 
                  :key="type.id" 
                  :value="type.id"
                >
                  {{ type.name }}
                </option>
              </Field>
              <ErrorMessage name="incident_type" class="error-feedback" />
            </div>
          </div>
          <div class="row m-b-sm">
            <h6>Time</h6>
            <hr />

            <div class="col-md-3 col-xs-6 col-xxs-12 m-b-sm">
              <label for="startDateFilter" class="form-label">{{
                $t("events.filters.start_date")
              }}</label>
              <Field
                type="date"
                name="start_date"
                class="form-control"
                id="startDateFilter"
                v-model="filters.start_date"
                ref="start_date"
                @change="addFocus('startDateFilter')"
              />
              <ErrorMessage name="start_date" class="error-feedback" />
            </div>
            <div class="col-md-3 col-xs-6 col-xxs-12 m-b-sm">
              <label for="endDateFilter" class="form-label">{{
                $t("events.filters.end_date")
              }}</label>
              <Field
                type="date"
                name="end_date"
                class="form-control"
                id="endDateFilter"
                v-model="filters.end_date"
                ref="end_date"
              />
              <ErrorMessage name="end_date" class="error-feedback" />
            </div>
            <div class="row m-b-sm">
              <div class="col-12 m-b-sm">
                <div class="col-md-12 reports-container">
                  <button class="btn btn-secondary m-h-sm" @click="updateWeek">
                    {{ $t("reports.week") }}
                  </button>
                  <button class="btn btn-secondary m-h-sm" @click="updateMonth">
                    {{ $t("reports.month") }}
                  </button>
                  <button class="btn btn-secondary m-h-sm" @click="updateQuarter">
                    {{ $t("reports.quarter") }}
                  </button>
                  <button class="btn btn-secondary m-h-sm" @click="updateYear">
                    {{ $t("reports.year") }}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="width-fit-content">
            <button
              type="submit"
              :disabled="loading"
              class="btn btn-primary m-h-sm"
              @click="
                showSecondButton = true;
                this.isShowingReports = true;
                this.isShowingTotals = false;
              "
            >
              <span
                v-show="loading"
                class="spinner-border spinner-border-sm m-r-xxs"
              ></span>
              <span>{{ $t("reports.generate_report") }} </span>
            </button>

            <button
              type="reset"
              :disabled="loading"
              class="btn btn-secondary m-h-sm"
              @click.prevent="clearFilter"
            >
              <span
                v-show="loading"
                class="spinner-border spinner-border-sm m-r-xxs"
              ></span>
              <span>{{ $t("global.clear_filter") }}</span>
            </button>
          </div>
        </Form>
      </div>
    </div>
  </div>

  <!-- Loading -->
  <div v-show="loading" style="width: 100%; text-align: center">
    <div class="spinner-border" style="height: 4rem; width: 4rem" role="status">
      <span class="visually-hidden">{{ $t("events.loading") }}...</span>
    </div>
  </div>

  <div v-if="!loading && reportData && isShowingReports" class="mt-4">
    <!-- Export buttons -->
    <div class="d-flex justify-content-end mb-3">
      <button class="btn btn-secondary m-r-sm" @click="exportPdf">
        Export PDF
      </button>
      <button class="btn btn-secondary m-r-sm" @click="exportExcel">
        Export Excel
      </button>
    </div>

    <!-- Summary Cards -->
    <div class="row">
      <div class="col-xl-3">
        <div class="card widget widget-stats-large">
          <div class="card-body">
            <div class="widget-stats-large-info">
              <div class="widget-stats-large-title">Total Faces</div>
              <div class="widget-stats-large-amount">
                {{ formatNumber(reportData.totals.detected_faces) }}
              </div>
              <div class="widget-stats-large-info-text">Total faces detected</div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-xl-3">
        <div class="card widget widget-stats-large">
          <div class="card-body">
            <div class="widget-stats-large-info">
              <div class="widget-stats-large-title">Alerted Faces</div>
              <div class="widget-stats-large-amount">
                {{ formatNumber(reportData.totals.alerted_faces) }}
              </div>
              <div class="widget-stats-large-info-text">Faces that triggered alerts</div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-xl-3">
        <div class="card widget widget-stats-large">
          <div class="card-body">
            <div class="widget-stats-large-info">
              <div class="widget-stats-large-title">Standard Faces</div>
              <div class="widget-stats-large-amount">
                {{ formatNumber(reportData.totals.standard_faces) }}
              </div>
              <div class="widget-stats-large-info-text">Faces without alerts</div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-xl-3">
        <div class="card widget widget-stats-large">
          <div class="card-body">
            <div class="widget-stats-large-info">
              <div class="widget-stats-large-title">Alert Rate</div>
              <div class="widget-stats-large-amount">
                {{ reportData.totals.alert_rate }}%
              </div>
              <div class="widget-stats-large-info-text">Percentage of faces that triggered alerts</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Time Series Chart -->
    <div class="row mt-4">
      <div class="col-xl-12">
        <div class="card">
          <div class="card-header">
            <h5 class="card-title">Face Detection Trends</h5>
            <div class="btn-group">
              <button 
                v-for="(view, index) in timeSeriesViews" 
                :key="index"
                class="btn btn-sm" 
                :class="currentTimeSeriesView === view.value ? 'btn-primary' : 'btn-outline-primary'"
                @click="setTimeSeriesView(view.value)"
              >
                {{ view.label }}
              </button>
            </div>
          </div>
          <div class="card-body">
            <vue-apex-charts
              height="350"
              :options="timeSeriesChartOptions"
              :series="timeSeriesSeries"
            ></vue-apex-charts>
          </div>
        </div>
      </div>
    </div>

    <!-- Charts Row 1 -->
    <div class="row mt-4">
      <!-- Incident Types Donut Chart -->
      <div class="col-xl-12">
        <div class="card">
          <div class="card-header">
            <h5 class="card-title">Incident Types Distribution</h5>
          </div>
          <div class="card-body">
            <vue-apex-charts
              height="350"
              :options="incidentTypesChartOptions"
              :series="incidentTypesSeries"
              style="color: black;"
            ></vue-apex-charts>
          </div>
        </div>
      </div>
    </div>

    <!-- Charts Row 2 -->
    <div class="row mt-4">
      <div class="card">
        <div class="card-header">
          <h5 class="card-title">Face vs Alert Comparison</h5>
        </div>
        <div class="card-body">
          <div class="face-comparison-container">
            <!-- Total Faces Bar -->
            <div class="mb-3">
              <div class="d-flex justify-content-between mb-1">
                <span>Total Faces</span>
                <span>{{ formatNumber(reportData.totals.detected_faces) }}</span>
              </div>
              <div class="progress" style="height: 30px;">
                <div class="progress-bar bg-primary" role="progressbar" style="width: 100%"></div>
              </div>
            </div>
            
            <!-- Alerted Faces Bar -->
            <div>
              <div class="d-flex justify-content-between mb-1">
                <span>Alerted Faces</span>
                <span>{{ formatNumber(reportData.totals.alerted_faces) }} ({{ reportData.totals.alert_rate }}%)</span>
              </div>
              <div class="progress" style="height: 30px;">
                <div 
                  class="progress-bar bg-warning" 
                  role="progressbar" 
                  :style="{width: reportData.totals.alert_rate + '%'}"
                ></div>
              </div>
            </div>
            
            <!-- Site-specific comparison -->
            <h6 class="mt-4">Site-specific Alert Rates</h6>
            <div v-for="(site, index) in reportData.report.slice(0, 5)" :key="site.site_id" class="mb-3">
              <div class="d-flex justify-content-between mb-1">
                <span>{{ site.site_name }}</span>
                <span>{{ site.alert_rate }}%</span>
              </div>
              <div class="progress" style="height: 20px;">
                <div class="progress-bar" role="progressbar" 
                  :class="index % 2 === 0 ? 'bg-info' : 'bg-success'"
                  :style="{width: site.alert_rate + '%'}"></div>
              </div>
            </div>
            <div v-if="reportData.report.length > 5" class="text-center mt-2">
              <small class="text-muted">Showing top 5 sites. See table below for all sites.</small>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div v-show="isClaimsEmpty" class="no-report-container m-t-md">
    <span> No Claims found that match your search results. </span>
  </div>
</template>

<script>
import axios from "axios";
import authHeader from "../../services/auth-header";
import { Form, Field, ErrorMessage } from "vee-validate";
import * as yup from "yup";
import ReportsService from "../../services/reports.service";
import Timestamp from "../../components/Timestamp.vue";
import ClientService from "../../services/client.service";
import { DateTime } from "luxon";
import { Chart, registerables } from "chart.js";
import Multiselect from "vue-multiselect";
import * as XLSX from "xlsx";
import MessageCard from "../../components/MessageCard.vue";
import VueApexCharts from "vue3-apexcharts";
import { jsPDF } from "jspdf";

Chart.register(...registerables);

export default {
  name: "ReportsFace",
  components: {
    Form,
    Field,
    ErrorMessage,
    Timestamp,
    Multiselect,
    MessageCard,
    VueApexCharts,
  },
  props: {
    site: Object,
    group: Number,
    start_date: String,
    end_date: String,
  },
  data() {
    const schema = yup.object().shape({
      site: yup.array().optional().nullable(),
      group: yup.number().optional().nullable(),
      start_date: yup.date().nullable().optional(),
      end_date: yup.date().nullable().optional(),
      alert_status: yup.string().nullable().optional(),
      incident_type: yup.number().nullable().transform(value => 
        (isNaN(value) || value === null || value === '') ? null : Number(value)
      ).optional(),
    });
    const baseChartConfig = {
      chart: {
        fontFamily: 'Poppins, sans-serif',
        toolbar: {
          show: true
        },
        zoom: {
          enabled: true
        }
      },
      theme: {
        mode: this.$store.state.auth.status.dark ? 'dark' : 'light',
      },
      tooltip: {
        theme: this.$store.state.auth.status.dark ? 'dark' : 'light',
      },
    };  
    return {
      sites: [],
      siteGroups: [],
      schema,
      filters: {
        site: [],
        start_date: null,
        end_date: null,
        group: null,
        alert_status: "",
        incident_type: "",
      },
      reportData: null,
      loading: false,
      isShowingReports: false,
      incidentTypes: [],  
      incidentTypesChartOptions: {
        chart: {
          id: 'incidentTypesChart',
          type: 'donut'
        },
        labels: [],
        colors: ['#ff9800', '#f44336', '#9c27b0', '#4caf50', '#3f51b5', '#e91e63'],
        legend: {
          position: 'bottom',
          formatter: function(seriesName, opts) {
            return seriesName + ": " + opts.w.globals.series[opts.seriesIndex].toLocaleString()
          }
        },
        dataLabels: {
          enabled: true,
          formatter: function(val, opts) {
            return opts.w.globals.series[opts.seriesIndex].toLocaleString() + ' (' + val.toFixed(1) + "%)"
          }
        },
        plotOptions: {
          pie: {
            donut: {
              size: '65%',
              labels: {
                show: true,
                total: {
                  show: true,
                  label: 'Total Alerts',
                  formatter: function (w) {
                    const total = w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                    return total.toLocaleString();
                  }
                }
              }
            }
          }
        },
        responsive: [{
          breakpoint: 480,
          options: {
            chart: {
              width: 300
            },
            legend: {
              position: 'bottom'
            }
          }
        }]
      },
      incidentTypesSeries: [],
      sitesFacesChartOptions: {
        chart: {
          id: 'sitesFacesChart',
          stacked: true
        },
        xaxis: {
          categories: [],
          labels: {
            rotate: -45,
            style: {
              fontSize: '12px'
            }
          }
        },
        colors: ['#2196f3', '#ff9800'],
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '70%',
          }
        },
        dataLabels: {
          enabled: false
        },
        legend: {
          position: 'top'
        }
      },
      sitesFacesSeries: [],
      sitesAlertRateChartOptions: {
        chart: {
          id: 'sitesAlertRateChart'
        },
        xaxis: {
          categories: [],
          labels: {
            rotate: -45,
            style: {
              fontSize: '12px'
            }
          }
        },
        colors: ['#ff9800'],
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '70%',
          }
        },
        dataLabels: {
          enabled: true,
          formatter: function(val) {
            return val.toFixed(1) + "%"
          }
        }
      },
      sitesAlertRateSeries: [],
      currentTimeSeriesView: 'all', 
      timeSeriesViews: [
        { label: 'All', value: 'all' },
        { label: 'Daily', value: 'daily' },
        { label: 'Weekly', value: 'weekly' },
        { label: 'Monthly', value: 'monthly' }
      ],
      timeSeriesChartOptions: {
        chart: {
          id: 'timeSeriesChart',
          type: 'line',
          height: 350,
          zoom: {
            enabled: true
          },
          toolbar: {
            show: true
          }
        },
        colors: ['#2196f3', '#ff9800', '#4caf50'],
        stroke: {
          width: 3,
          curve: 'smooth'
        },
        xaxis: {
          type: 'datetime',
          labels: {
            format: 'dd MMM',
            datetimeUTC: false
          }
        },
        yaxis: {
          title: {
            text: 'Face Count'
          },
          min: 0
        },
        legend: {
          position: 'top'
        },
        markers: {
          size: 4,
          hover: {
            size: 6
          }
        },
        tooltip: {
          theme: "dark",
          x: {
            format: 'dd MMM yyyy'
          }
        },
        dataLabels: {
          enabled: false
        }
      },
      timeSeriesSeries: []
    };
  },
  mounted() {
    this.init();
    this.getTypes();
  },
  methods: {
    async init() {
      await this.loadSites();
      await this.loadSiteGroups();

      if (this.isValid(this.site)) {
        let siteSearch = this.sites.filter((s) => {
          return parseInt(s.id) === parseInt(this.site);
        })[0];
        if (siteSearch) {
          this.filters.site.push(siteSearch);
        }
      }

      if (this.isValid(this.group)) {
        let groupSearch = this.siteGroups.filter((sg) => {
          return parseInt(sg.id) === parseInt(this.group);
        })[0];
        if (groupSearch) {
          this.filters.group = groupSearch;
        }
      }

      if (this.isValid(this.start_date)) {
        this.filters.start_date = this.start_date;
      }
      if (this.isValid(this.end_date)) {
        this.filters.end_date = this.end_date;
      }

      if (this.site || this.start_date || this.end_date) {
        this.generate(this.filters);
      }
    },
    generate(values) {
      this.loading = true;

      let sites;
      if (this.filters.site) {
        if (Array.isArray(this.filters.site)) {
          sites = this.filters.site.map(obj => obj.id);
        } else if (this.filters.site.id) {
          sites = [this.filters.site.id];
        } else {
          sites = null;
        }
      } else {
        sites = null;
      }

      let start_date = values.start_date
        ? DateTime.fromFormat(values.start_date, "yyyy-MM-dd").toFormat("dd/MM/yyyy")
        : null;

      let end_date = values.end_date
        ? DateTime.fromFormat(values.end_date, "yyyy-MM-dd").toFormat("dd/MM/yyyy")
        : null;

      ReportsService.getFaceReport(
        parseInt(this.$store.state.auth.user.selectedClient),
        {
          sites: sites,
          startDate: start_date || null, 
          endDate: end_date || null, 
          alertStatus: this.filters.alert_status || null,
          incidentType: this.filters.incident_type ? parseInt(this.filters.incident_type) : null,
          group: this.filters.group?.id || null,
        }
      )
        .then((response) => {
          this.reportData = response.data;
          this.updateCharts();
        })
        .catch((error) => {
          this.$error("Failed to generate report", error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    updateCharts() {
      if (!this.reportData) return;
      
      const incidentTypes = {};
      let totalIncidents = 0;
      
      this.reportData.report.forEach(site => {
        Object.entries(site.incidents).forEach(([type, count]) => {
          incidentTypes[type] = (incidentTypes[type] || 0) + count;
          totalIncidents += count;
        });
      });
      
      this.incidentTypesChartOptions.labels = Object.keys(incidentTypes);
      this.incidentTypesSeries = Object.values(incidentTypes);
      
      const siteNames = this.reportData.report.map(site => site.site_name);
      const standardFaces = this.reportData.report.map(site => site.standard_faces);
      const alertedFaces = this.reportData.report.map(site => site.alerted_faces);
      
      this.sitesFacesChartOptions.xaxis.categories = siteNames;
      this.sitesFacesSeries = [
        {
          name: 'Standard Faces',
          data: standardFaces
        },
        {
          name: 'Alerted Faces',
          data: alertedFaces
        }
      ];
      
      const alertRates = this.reportData.report.map(site => parseFloat(site.alert_rate));
      
      this.sitesAlertRateChartOptions.xaxis.categories = siteNames;
      this.sitesAlertRateSeries = [
        {
          name: 'Alert Rate',
          data: alertRates
        }
      ];
      
      this.updateTimeSeriesChart();
    },
    updateTimeSeriesChart() {
      if (!this.reportData || !this.reportData.time_series || this.reportData.time_series.length === 0) return;
      
      let data = [...this.reportData.time_series];
      const timeRange = this.getTimeRangeInDays(data);
      
      if (this.currentTimeSeriesView === 'weekly' || (this.currentTimeSeriesView === 'all' && timeRange > 90)) {
        data = this.aggregateTimeSeriesByWeek(data);
      } else if (this.currentTimeSeriesView === 'monthly' || (this.currentTimeSeriesView === 'all' && timeRange > 365)) {
        data = this.aggregateTimeSeriesByMonth(data);
      }
      
      if (timeRange > 365) {
        this.timeSeriesChartOptions = {
          ...this.timeSeriesChartOptions,
          xaxis: {
            ...this.timeSeriesChartOptions.xaxis,
            labels: {
              format: 'MMM yyyy'
            }
          }
        };
      } else if (timeRange > 60) {
        this.timeSeriesChartOptions = {
          ...this.timeSeriesChartOptions,
          xaxis: {
            ...this.timeSeriesChartOptions.xaxis,
            labels: {
              format: 'dd MMM'
            }
          }
        };
      }
      
      this.timeSeriesSeries = [
        {
          name: 'Detected Faces',
          data: data.map(item => ({
            x: new Date(item.date).getTime(),
            y: item.detected
          }))
        },
        {
          name: 'Alerted Faces',
          data: data.map(item => ({
            x: new Date(item.date).getTime(),
            y: item.alerted
          }))
        },
        {
          name: 'Standard Faces',
          data: data.map(item => ({
            x: new Date(item.date).getTime(),
            y: item.normal
          }))
        }
      ];
    },
    getTimeRangeInDays(timeSeriesData) {
      if (!timeSeriesData || timeSeriesData.length < 2) return 0;
      
      const startDate = new Date(timeSeriesData[0].date);
      const endDate = new Date(timeSeriesData[timeSeriesData.length - 1].date);
      
      return Math.round((endDate - startDate) / (24 * 60 * 60 * 1000));
    },
    aggregateTimeSeriesByWeek(timeSeriesData) {
      const weeklyData = {};
      
      timeSeriesData.forEach(item => {
        const date = new Date(item.date);
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        const weekKey = weekStart.toISOString().split('T')[0];
        
        if (!weeklyData[weekKey]) {
          weeklyData[weekKey] = {
            date: weekKey,
            detected: 0,
            alerted: 0,
            normal: 0
          };
        }
        
        weeklyData[weekKey].detected += item.detected;
        weeklyData[weekKey].alerted += item.alerted;
        weeklyData[weekKey].normal += item.normal;
      });
      
      return Object.values(weeklyData).sort((a, b) => new Date(a.date) - new Date(b.date));
    },
    aggregateTimeSeriesByMonth(timeSeriesData) {
      const monthlyData = {};
      
      timeSeriesData.forEach(item => {
        const date = new Date(item.date);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`;
        
        if (!monthlyData[monthKey]) {
          monthlyData[monthKey] = {
            date: monthKey,
            detected: 0,
            alerted: 0,
            normal: 0
          };
        }
        
        monthlyData[monthKey].detected += item.detected;
        monthlyData[monthKey].alerted += item.alerted;
        monthlyData[monthKey].normal += item.normal;
      });
      
      return Object.values(monthlyData).sort((a, b) => new Date(a.date) - new Date(b.date));
    },
    getTypes() {
      axios
        .get(`https://api.varsanpr.com/customer/incident/incidents/types`, {
          headers: authHeader(),
        })
        .then((response) => {
          this.incidentTypes = response.data.types;
        })
        .catch((e) => {
          console.log(e);
        });
    },
    setTimeSeriesView(view) {
      this.currentTimeSeriesView = view;
      this.updateTimeSeriesChart();
    },
    async loadSites(group = null) {
      return ClientService.getClientSites(
        this.$store.state.auth.user.selectedClient,
        false,
        group?.id || null,
        5
      )
        .then((response) => {
          this.sites = response.data.sort((a, b) => {
            if (a.name < b.name) {
              return -1;
            }
            if (a.name > b.name) {
              return 1;
            }
            return 0;
          });
          this.sites.forEach((site) => {
            if (site.customer_reference) {
              site.name = "(" + site.customer_reference + ") " + site.name;
            }
          });
        })
        .catch((error) => {
          this.$error(this.$t("sites.site_load_error"), error);
        });
    },
    async loadSiteGroups() {
      return ClientService.getClientSiteGroups(
        this.$store.state.auth.user.selectedClient
      )
        .then((response) => {
          this.siteGroups = response.data.sort((a, b) => {
            if (a.name < b.name) {
              return -1;
            }
            if (a.name > b.name) {
              return 1;
            }
            return 0;
          });
        })
        .catch((error) => {
          this.$error(this.$t("sites.site_load_error"), error);
        });
    },
    updateWeek(event) {
      event.preventDefault();
      this.filters.start_date = DateTime.local()
        .startOf("week")
        .toFormat("yyyy-MM-dd");
      this.filters.end_date = DateTime.local()
        .endOf("week")
        .toFormat("yyyy-MM-dd");
    },
    updateMonth(event) {
      event.preventDefault();
      this.filters.start_date = DateTime.local()
        .startOf("month")
        .toFormat("yyyy-MM-dd");
      this.filters.end_date = DateTime.local()
        .endOf("month")
        .toFormat("yyyy-MM-dd");
    },
    updateQuarter(event) {
      event.preventDefault();
      this.filters.start_date = DateTime.local()
        .startOf("quarter")
        .toFormat("yyyy-MM-dd");
      this.filters.end_date = DateTime.local()
        .endOf("quarter")
        .toFormat("yyyy-MM-dd");
    },
    updateYear(event) {
      event.preventDefault();
      this.filters.start_date = DateTime.local()
        .startOf("year")
        .toFormat("yyyy-MM-dd");
      this.filters.end_date = DateTime.local()
        .endOf("year")
        .toFormat("yyyy-MM-dd");
    },
    exportExcel() {
      if (!this.reportData) return;
          
      const workbook = XLSX.utils.book_new();
      
      const summaryData = [
        {
          'Metric': 'Total Faces',
          'Value': this.reportData.totals.detected_faces
        },
        {
          'Metric': 'Alerted Faces',
          'Value': this.reportData.totals.alerted_faces
        },
        {
          'Metric': 'Standard Faces',
          'Value': this.reportData.totals.standard_faces
        },
        {
          'Metric': 'Alert Rate',
          'Value': this.reportData.totals.alert_rate + '%'
        }
      ];
      
      const incidentTypes = {};
      this.reportData.report.forEach(site => {
        Object.entries(site.incidents).forEach(([type, count]) => {
          incidentTypes[type] = (incidentTypes[type] || 0) + count;
        });
      });
      
      Object.entries(incidentTypes).forEach(([type, count]) => {
        summaryData.push({
          'Metric': 'Incident: ' + type,
          'Value': count
        });
      });
      
      const summarySheet = XLSX.utils.json_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");
      
      if (this.reportData.time_series && this.reportData.time_series.length > 0) {
        const timeSeriesData = this.reportData.time_series.map(item => ({
          'Date': item.date,
          'Detected Faces': item.detected,
          'Alerted Faces': item.alerted,
          'Standard Faces': item.normal,
          'Alert Rate (%)': item.detected > 0 ? ((item.alerted / item.detected) * 100).toFixed(2) : '0.00'
        }));
        
        const timeSeriesSheet = XLSX.utils.json_to_sheet(timeSeriesData);
        
        const range = XLSX.utils.decode_range(timeSeriesSheet['!ref']);
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const address = XLSX.utils.encode_col(C) + "1";
          if (!timeSeriesSheet[address]) continue;
          timeSeriesSheet[address].s = { fill: { fgColor: { rgb: "4F81BD" } }, font: { bold: true, color: { rgb: "FFFFFF" } } };
        }
        
        XLSX.utils.book_append_sheet(workbook, timeSeriesSheet, "Time Series");
      }
      
      const sitesData = this.reportData.report.map(site => {
        const incidents = Object.entries(site.incidents)
          .map(([type, count]) => `${type}: ${count}`)
          .join(', ');
          
        return {
          'Site': site.site_name,
          'Total Faces': site.detected_faces,
          'Alerted Faces': site.alerted_faces,
          'Standard Faces': site.standard_faces,
          'Alert Rate (%)': site.alert_rate,
          'Incident Types': incidents || 'None'
        };
      });
      
      const sitesSheet = XLSX.utils.json_to_sheet(sitesData);
      XLSX.utils.book_append_sheet(workbook, sitesSheet, "Sites Data");
      
      XLSX.writeFile(workbook, `FaceDetectionReport-${DateTime.local().toFormat("yyyyMMdd")}.xlsx`);
    },
    exportPdf() {
      if (!this.reportData) return;
      
      const doc = new jsPDF({ orientation: 'landscape' });
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 10;
      let yPos = margin;

      doc.setFontSize(18);
      doc.setTextColor(40, 40, 40);
      doc.text(`VARS Face Detection Report`, pageWidth/2, yPos, { align: 'center' });
      yPos += 10; 

      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`Generated: ${DateTime.local().toFormat("dd MMMM yyyy HH:mm")}`, pageWidth/2, yPos, { align: 'center' });
      yPos += 18; 

      doc.setFillColor(240, 240, 240);
      doc.rect(margin, yPos, pageWidth - margin * 2, 40, 'F');
      
      doc.setFontSize(14);
      doc.setTextColor(40, 40, 40);
      doc.text("Summary Totals", margin + 5, yPos + 10);
      
      doc.setFontSize(10);
      doc.text(`Total Faces: ${this.formatNumber(this.reportData.totals.detected_faces)}`, margin + 5, yPos + 20);
      doc.text(`Alerted Faces: ${this.formatNumber(this.reportData.totals.alerted_faces)}`, pageWidth/2, yPos + 20);
      doc.text(`Standard Faces: ${this.formatNumber(this.reportData.totals.standard_faces)}`, margin + 5, yPos + 30);
      doc.text(`Alert Rate: ${this.reportData.totals.alert_rate}%`, pageWidth/2, yPos + 30);
      
      yPos += 50;

      doc.setFontSize(10);
      doc.setTextColor(80, 80, 80);
      
      let hasFilters = (this.filters.start_date && this.filters.end_date) || 
                      (this.filters.site && this.filters.site.name) ||
                      this.filters.alert_status;

      if (hasFilters) {
        doc.setFontSize(10);
        doc.setTextColor(80, 80, 80);
        
        let filterText = "Filters: ";
        if (this.filters.start_date && this.filters.end_date) {
          filterText += `Date Range: ${this.filters.start_date} to ${this.filters.end_date}`;
        }
        if (this.filters.site && this.filters.site.name) {
          filterText += `${filterText.length > 9 ? ' | ' : ''}Site: ${this.filters.site.name}`;
        }
        if (this.filters.alert_status) {
          filterText += `${filterText.length > 9 ? ' | ' : ''}Alert Status: ${this.filters.alert_status}`;
        }
        
        doc.text(filterText, margin, yPos);
        yPos += 15;
      }

      doc.setFontSize(14);
      doc.setTextColor(40, 40, 40);
      doc.text("Incident Types Breakdown", margin, yPos);
      yPos += 10;
      
      const incidentTypes = {};
      this.reportData.report.forEach(site => {
        Object.entries(site.incidents).forEach(([type, count]) => {
          incidentTypes[type] = (incidentTypes[type] || 0) + count;
        });
      });
      
      if (Object.keys(incidentTypes).length > 0) {
        let incidentYPos = yPos;
        doc.setFontSize(10);
        
        Object.entries(incidentTypes).forEach(([type, count], index) => {
          doc.text(`${type}: ${count}`, margin + (index % 3) * 80, incidentYPos);
          if (index % 3 === 2) incidentYPos += 8;
        });
        
        yPos = incidentYPos + 20; 
      } else {
        doc.setFontSize(10);
        doc.text("No incident data available", margin, yPos);
        yPos += 20;
      }

      if (this.reportData.time_series && this.reportData.time_series.length > 0) {
        if (yPos > pageHeight - 120) { 
          doc.addPage();
          yPos = margin + 15; 
        }
        
        doc.setFontSize(14);
        doc.setTextColor(40, 40, 40);
        doc.text("Daily Face Detection Data", margin, yPos);
        yPos += 20; 
        
        doc.setFillColor(89, 142, 247);
        doc.setDrawColor(89, 142, 247);
        doc.setTextColor(255, 255, 255);
        doc.rect(margin, yPos, pageWidth - margin * 2, 10, 'F');
        
        const availableWidth = pageWidth - (margin * 2) - 10; 
        
        const columns = [
          { title: "Date", width: Math.floor(availableWidth * 0.20) }, 
          { title: "Detected Faces", width: Math.floor(availableWidth * 0.20) }, 
          { title: "Alerted Faces", width: Math.floor(availableWidth * 0.20) }, 
          { title: "Standard Faces", width: Math.floor(availableWidth * 0.20) }, 
          { title: "Alert Rate (%)", width: Math.floor(availableWidth * 0.20) } 
        ];
        
        doc.setFontSize(10);
        let xPos = margin + 5;
        columns.forEach(col => {
          doc.text(col.title, xPos, yPos + 7);
          xPos += col.width;
        });
        yPos += 15;
        
        doc.setTextColor(40, 40, 40);
        
        const timeSeriesData = [...this.reportData.time_series];
        
        timeSeriesData.forEach((item, index) => {
          if (yPos > pageHeight - 20) {
            doc.addPage();
            yPos = margin + 15;
            
            doc.setFillColor(89, 142, 247);
            doc.setDrawColor(89, 142, 247);
            doc.setTextColor(255, 255, 255);
            doc.rect(margin, yPos, pageWidth - margin * 2, 10, 'F');
            
            doc.setFontSize(10);
            let xPos = margin + 5;
            columns.forEach(col => {
              doc.text(col.title, xPos, yPos + 7);
              xPos += col.width;
            });
            yPos += 15; 
            
            doc.setTextColor(40, 40, 40);
          }
          
          if (index % 2 === 1) {
            doc.setFillColor(245, 245, 245);
            doc.rect(margin, yPos - 5, pageWidth - margin * 2, 10, 'F');
          }
          
          const formattedDate = DateTime.fromISO(item.date).toFormat('dd MMM yyyy');
          
          xPos = margin + 5;
          doc.text(formattedDate, xPos, yPos);
          xPos += columns[0].width;
          
          doc.text(item.detected.toString(), xPos, yPos);
          xPos += columns[1].width;
          
          doc.text(item.alerted.toString(), xPos, yPos);
          xPos += columns[2].width;
          
          doc.text(item.normal.toString(), xPos, yPos);
          xPos += columns[3].width;
          
          const alertRate = item.detected > 0 ? ((item.alerted / item.detected) * 100).toFixed(2) : '0.00';
          doc.text(alertRate + '%', xPos, yPos);
          
          yPos += 10;
        });
      }

      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text(`VARS Face Detection Report - Generated by VARS Portal`, margin, pageHeight - 10);
      doc.text(`Page ${doc.internal.getNumberOfPages()}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
      
      doc.save(`FaceDetectionReport-${DateTime.local().toFormat("yyyyMMdd")}.pdf`);
    },
    clearFilter() {
      this.filters = {
        site: null,
        start_date: null,
        end_date: null,
        group: null,
        alert_status: "",
        incident_type: "",
      };
    },
    getTimeFormat(timeRange) {
      if (timeRange > 365) return 'MMM yyyy';
      if (timeRange > 31) return 'dd MMM';
      return 'dd MMM HH:mm';
    },
    toggleShowCharts() {
      this.showCharts = !this.showCharts;
    },
    isValid(value) {
      if (value === null) return false;
      if (value === undefined) return false;
      if (typeof value === "string" && value.length < 1) return false;
      return true;
    },
    formatNumber(number) {
      return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
  },
};
</script>

<style scoped>
td > span {
  display: block;
}

.btn {
  padding: 10px 20px;
}

.table-heading {
  padding-top: 0;
  margin-bottom: 20px;
}

.reports-container {
  height: fit-content;
  margin-top: 25px;
}

.form-check-label {
  color: #cdc4d1;
  font-size: 16px;
  font-weight: 100;
}

.form-check-input:checked + .form-check-label {
  color: white;
}

.form-check-input {
  border-radius: 0;
  margin-right: 15px;
  width: 20px;
  height: 20px;
}

*:not(.form-switch) > .form-check-input:not(:checked) {
  background: #cdc4d1;
  border: 1px solid #cdc4d1;
}

.btn-secondary:focus {
  background-color: #598ef7;
  border-color: #598ef7;
}

.no-report-container {
  width: 100%;
  text-align: center;
  font-style: italic;
  color: #ff9500;
  font-size: 16px;
}

.width-fit-content {
  margin: auto;
  margin-top: 25px;
}

.temp {
  width: 30px;
  height: 30px;
  border-radius: 50%;
}

.temp:hover {
  cursor: pointer;
}

.modal-sub-title {
  font-weight: 100;
}

.form-checkbox {
  padding: 5px;
  margin-right: 10px;
}

.checkbox-container {
  font-weight: 300;
  margin-top: 1rem;
}

.checkbox-container input {
  width: 12px;
  height: 12px;
}

.display-none {
  display: none;
}

#statusFilter input {
  border: none;
}

@media (min-width: 1440px) {
  .col-12 {
    width: 57%;
  }
}

@media (max-width: 768px) {
  .m-h-sm {
    margin-top: 10px;
  }
}
.face-comparison-container {
  padding: 1rem;
}

.progress {
  background-color: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
}

.progress-bar {
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  white-space: nowrap;
  transition: width 0.6s ease;
}

.bg-primary {
  background-color: #2269f5 !important;
}

.bg-warning {
  background-color: #ff9800 !important;
}

.bg-info {
  background-color: #2196f3 !important;
}

.bg-success {
  background-color: #4caf50 !important;
}
@media (max-width: 992px) {
  .btn-secondary, .btn-primary {
    width: 100%;
    margin-bottom: 10px;
    align-items: center;
    justify-content: center;
    display: flex;
    flex-direction: column;
    padding: 10px;
  
  }
}
</style>
